/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';
import { SectionCard } from '@features/property-management/components/PropertyDetailView/SectionCard';
import type { ClientRow } from '../../context/ClientManagementContext';
import { formatPrefValue, getPrefLabels, resolvePrefLabel } from './helpers';

export interface PreferencesCardProps {
  client: ClientRow;
}

/** Preference keys that are provenance, not a preference. */
const NOT_A_PREFERENCE = new Set(['lead_origin', 'lead_interactions']);

/**
 * The client's stated preferences as chips.
 *
 * One chip per preference rather than a label/value list: they are a
 * shopping list, not a record, and a broker scans them for the two or
 * three that rule a listing in or out.
 *
 * @param props - The client whose preferences are shown.
 * @returns The card, or nothing when the client stated none.
 */
export function PreferencesCard({ client }: PreferencesCardProps) {
  const { t } = useTranslation('features/clients');
  const prefs = client.preferences as Record<string, unknown> | undefined;
  const labels = getPrefLabels();
  const hasPriceRange = Boolean(prefs && (prefs.price_min || prefs.price_max));

  const entries = Object.entries(prefs ?? {}).filter(([key, value]) => {
    if (NOT_A_PREFERENCE.has(key)) return false;
    if (hasPriceRange && (key === 'price_min' || key === 'price_max')) return false;
    return value != null && value !== '' && !(Array.isArray(value) && value.length === 0);
  });

  const chips = entries.map(([key, value]) => `${resolvePrefLabel(labels, key)}: ${formatPrefValue(key, value)}`);
  if (hasPriceRange) {
    chips.unshift(
      `${t('profile.overview.priceRange')} ${formatPrefValue('price_min', prefs!.price_min || 0)} a ${formatPrefValue('price_max', prefs!.price_max || 0)}`,
    );
  }

  if (chips.length === 0) return null;

  return (
    <SectionCard data-testid="client-preferences" title={t('profile.overview.preferences')}>
      <div className="flex flex-wrap gap-[6px]">
        {chips.map((chip) => (
          <Text
            key={chip}
            component="span"
            className="rounded-(--radius-md) border border-solid border-[color-mix(in_srgb,var(--seed-base)_15%,transparent)] bg-[color-mix(in_srgb,var(--seed-base)_6%,var(--surface-primary))] px-(--space-2-5) py-[3px] text-[length:var(--text-meta)] leading-[var(--leading-snug)] text-(--text-primary)"
          >
            {chip}
          </Text>
        ))}
      </div>
    </SectionCard>
  );
}
